import { DOCUMENT, Injectable, LOCALE_ID, RESPONSE_INIT, inject } from '@angular/core';

import { ActivatedRouteSnapshot, RedirectCommand, Router, RouterStateSnapshot } from '@angular/router';
import { isPageWithoutCountries, Page, PageType } from '../types/page';
import { Observable, combineLatest, of } from 'rxjs';
import { ContentRepositoryService, MetaRefreshRedirectError } from './content-repository.service';
import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, first, switchMap, tap } from 'rxjs/operators';
import { LocaleService } from './locale.service';
import { SettingsService } from './settings.service';
import { GLOBAL_REGION } from './settings-constants';
import { PageService } from './page.service';
import { getLogger } from '@knorr-bremse-portals/ngx-components';
import { Loggers } from '../../express.tokens';
import { BaseLocationService } from './base-location.service';

@Injectable({
  providedIn: 'root',
})
export class DynamicPageResolver  {
  private contentRepositoryService = inject(ContentRepositoryService);
  private locale = inject(LocaleService);
  private settings = inject(SettingsService);
  private location = inject(Location);
  private baseLocation = inject(BaseLocationService);
  private document = inject(DOCUMENT);
  private router = inject(Router);
  private logger = getLogger(Loggers.Renderer);
  private pageService = inject(PageService);
  private localeId = inject(LOCALE_ID);
  private response = inject(RESPONSE_INIT);

  public resolve(route: ActivatedRouteSnapshot, routerState: RouterStateSnapshot): Observable<Page | RedirectCommand> {
    this.logger.debug('Starting page resolver with url', { routerStateUrl: routerState.url });
    const url = this.getURL(routerState.url);
    const path = url.pathname;
    this.logger.debug('Escaped url path to', { escapedPath: path });
    this.pageService.setLoading();

    return this.contentRepositoryService.fetchPage<Page>(path, true).pipe(
      tap(page => {
        if (page.sections === undefined) {
          this.logger.debug('Invalid page requested, responding with 404', { path: path });
          throw new HttpErrorResponse({
            status: 404,
            statusText: 'Not Found',
          });
        }
      }),
      catchError((error) => {
        if (error instanceof MetaRefreshRedirectError) {
          return of(this.handleMetaRefreshRedirect(error.location));
        }

        if (!(error instanceof HttpErrorResponse)) {
          return this.contentRepositoryService.handleError(error, this.localeId, undefined, true);
        }

        if (error.status !== 404) {
          return this.contentRepositoryService.handleHttpError(error, this.localeId, undefined, true);
        }

        return combineLatest([
          this.settings.countrySettingUrls,
          this.settings.fallbackCountry,
          this.settings.fallbackCountryMapping,
          this.settings.fallbackLanguage,
        ]).pipe(
          first(),
          switchMap(([countrySettingUrls, fallbackRegion, fallbackCountryMapping, fallbackLanguage]) => {
            const globalCountryLanguages = this.locale.convertCountryLanguageUrlsToCountryLanguages(countrySettingUrls);
            if (this.urlStartsWithCountryLanguage(path, globalCountryLanguages)) {
              return this.contentRepositoryService.handleHttpError(error, this.localeId, undefined, true);
            }

            let fallbackUrl = `/${fallbackLanguage}`;
            if (fallbackRegion && fallbackRegion !== GLOBAL_REGION) {
              fallbackUrl += `/${fallbackRegion.toLowerCase()}`;
            }
            fallbackUrl += path;

            this.logger.debug('Trying to fetch fallback url', { fallbackUrl: fallbackUrl });

            return this.contentRepositoryService.fetchPage<Page>(fallbackUrl, true).pipe(
              catchError((innerError) => {
                if (innerError instanceof MetaRefreshRedirectError) {
                  return of(this.handleMetaRefreshRedirect(innerError.location));
                }

                return this.contentRepositoryService.handleError(innerError, fallbackLanguage, fallbackRegion === GLOBAL_REGION ? undefined : fallbackRegion, true)
              }),
              switchMap((page) => {
                if (page instanceof RedirectCommand) {
                  return of(page);
                }

                let optimalRegion: string | undefined;
                let optimalLanguage: string;
                let optimalUrl: string;
                if (isPageWithoutCountries(page)) {
                  optimalLanguage = this.locale.getOptimalLanguage(Object.keys(page.languages), fallbackLanguage);
                  optimalUrl = page.languages[optimalLanguage];
                } else {
                  const pageCountryLanguages = this.locale.convertCountryLanguageUrlsToCountryLanguages(page.countryLanguageCombinations);
                  this.logger.debug('Page available country & languages', { countryLanguages: pageCountryLanguages });
                  const optimalLocale = this.locale.getOptimalLocale(pageCountryLanguages, fallbackLanguage, fallbackRegion, fallbackCountryMapping);
                  optimalLanguage = optimalLocale.language;
                  optimalRegion = optimalLocale.region;
                  optimalUrl = page.countryLanguageCombinations[optimalLocale.region][optimalLocale.language];
                }

                const originalQueryParams = url.search.substring(1);
                const originalFragment = url.hash;

                if (optimalRegion === fallbackRegion && optimalLanguage === fallbackLanguage) {
                  this.logger.debug('Optimal region & language = fallback', { fallbackUrl: fallbackUrl });
                  this.location.replaceState(fallbackUrl + originalFragment, originalQueryParams);
                  return of(page);
                }

                const escapedOptimalUrl = this.getURL(optimalUrl).pathname;

                this.logger.debug('Trying to fetch optimal url', { optimalUrl: optimalUrl, escapedOptimalUrl: escapedOptimalUrl });

                this.location.replaceState(escapedOptimalUrl + originalFragment, originalQueryParams);
                return this.contentRepositoryService.fetchPage<Page>(escapedOptimalUrl, true).pipe(
                  catchError((innerError) => {
                    if (innerError instanceof MetaRefreshRedirectError) {
                      return of(this.handleMetaRefreshRedirect(innerError.location));
                    }

                    return this.contentRepositoryService.handleError(innerError, optimalLanguage, optimalRegion, true)
                  }),
                );
              }),
            );
          }),
        );

      }),
      tap(page => {
        if (page instanceof RedirectCommand) {
          return;
        }

        page.path = path;
      }),
    );

  }

  private getURL(url: string): URL {
    return (new URL(url, 'https://fakebase'));
  }

  private handleMetaRefreshRedirect(target: string): Page | RedirectCommand {
    this.logger.debug('Handling meta refresh redirect', {
      target,
    });

    const targetUrl = this.getURL(target);
    if (targetUrl.origin === this.baseLocation.origin) {
      return new RedirectCommand(
        this.router.parseUrl(target.replace(targetUrl.origin, '')),
        {
          replaceUrl: true,
        },
      );
    }

    if (this.response !== null) {
      this.response.status = 301;
      this.response.headers = {
        'Vary': 'Accept-Language', // Let proxies, clients etc. know that this should only be cached in combination with these headers
        'Location': target,
      }
    } else {
      this.document.location.href = target;
    }

    return this.createRedirectPlaceholderPage();
  }

  private createRedirectPlaceholderPage(): Page {
    return {
      type: PageType.Website,
      path: '',
      canonical: '',
      title: '',
      description: '',
      modifiedDate: '',
      currentLanguage: this.localeId,
      sections: [
        {
          component: 'filler',
          anchor: '',
          attributes: {
            numColumns: 12,
            noMarginBottom: true,
          },
        },
      ],
      excludeFromSearch: true,
      disableInitialCookieDialog: true,
      languages: {
        [this.localeId]: '',
      },
    };
  }

  private urlStartsWithCountryLanguage(url: string, countryLanguages: {[key: string]: string[]}): boolean {
    for (const [country, languages] of Object.entries(countryLanguages)) {
      if (country !== GLOBAL_REGION) {
        if (languages.some(language => url.startsWith(`/${language}/${country}`))) {
          return true;
        }
      } else {
        if (languages.some(language => url.startsWith(`/${language}`))) {
          return true;
        }
      }
    }
    return false;
  }

}
